import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  TextField,
  Select,
} from 'src/ui/components';

import { formatSelectOptions } from 'src/utils/common';
import { useGetStateProvince } from 'src/hooks';
import { useGetCountry } from 'src/hooks';
import { useGetAddress } from 'src/hooks';
import { usePersonRepoStore } from 'src/features/PersonRepository/store/personRepoStore';

import { ResponseType } from 'src/features/PersonRepository/types/QuestionsTabType';

export function AddressEdit({ question }: { question: ResponseType }) {
  
  const qnTempId = useMemo(() => {
    return question?.TemplateId || 0;
  }, [question]);

  const [showCard, setShowCard] = useState<boolean>(false);
  const [postalCode, setPostalCode] = useState<string>('');

  const { data: stateprovince } = useGetStateProvince();
  const { data: country } = useGetCountry();
  const { data: postalAddressList } = useGetAddress(postalCode);

  const updateAddressDetailsInQnTab = usePersonRepoStore(
    useCallback(state => state?.updateAddressDetailsInQnTab, []),
  );
  const addressObject = usePersonRepoStore(
    useCallback(state => state.formValues.Poc?.Address?.[qnTempId], [qnTempId]),
  );

  function handleRowClick(item: any) {
    let address = {
      ...addressObject,
      City: item?.PlaceName ? item?.PlaceName : '',
      StateProvinceId: item?.StateProvince?.Id,
      StateProvince: item?.StateProvince,
      countries: [],
      stateProvinces: [],
      ZipPostalCode: item?.Code,
      CountryId: item?.Country?.Id,
      Country: item?.Country,
      County: item?.County,
    };
    updateAddressDetailsInQnTab(address, qnTempId);
    setShowCard(false);
  }

  const formattedAddressDetails = usePersonRepoStore(
    React.useCallback(
      state =>
        state?.formValues?.Poc?.Address &&
        state?.formValues?.Poc?.Address[qnTempId],
      [qnTempId],
    ),
  );
  const AddressRef = useRef();

  React.useEffect(() => {
    AddressRef.current = formattedAddressDetails;
  }, [formattedAddressDetails]);

  const stateProvinceDropDownList = formatSelectOptions(stateprovince?.Data);
  const countryDropDownList = formatSelectOptions(country?.Data);

  function renderTextField({ id, label, value, onChange }: {
    id: string;
    label: string;
    value: string;
    onChange: any;
  }) {
    return (
      <TextField id={id} label={label} value={value} onChange={onChange} />
    );
  }

  return (
    <dd className="flex gap-2 border-b dark:border-dark-medium">
      <h4 className="w-1/3 px-3 py-2 text-sm font-medium">
        {question?.FieldLabel}
      </h4>
      <p className="w-4/6 space-y-4 px-3 py-2">
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
          <div className="relative">
            <TextField
              id="search-by-zip"
              label="Search by zip/postal code"
              type="search"
              placeholder="Search by zip/postal code"
              onChange={e => {
                setPostalCode(e?.target?.value);
                setShowCard(true);
              }}
              value={postalCode}
            />
            {postalAddressList && postalAddressList?.length > 0 && showCard && (
              <div className="absolute inset-x-0 top-full mt-2 max-h-52 overflow-y-auto overflow-x-hidden rounded-lg border bg-white shadow-lg dark:border-neutral-600 dark:bg-neutral-700">
                <ul className="divide-y text-black dark:divide-neutral-800 dark:text-white ">
                  {postalAddressList?.map((item, index) => {
                    return (
                      <li
                        className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-neutral-600"
                        key={`${item?.Code}${index}`}
                        onClick={() => {
                          handleRowClick(item);
                        }}
                      >
                        <p className="truncate text-sm font-medium">
                          {item?.Code}:{item?.PlaceName}
                        </p>
                        <p className="text-sm opacity-60">
                          {item?.StateProvince?.Name},{item?.Country?.Name}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
         {renderTextField({
            id: 'street1',
            label: 'Street 1',
            value: formattedAddressDetails?.Street1
            ? formattedAddressDetails?.Street1
            : '',
            onChange: (e:any) =>
              updateAddressDetailsInQnTab(
                { Street1: e.target.value },
                qnTempId,
              ),
          })}
         {renderTextField({
            id: 'street2',
            label: 'Street 2',
            value: formattedAddressDetails?.Street2
            ? formattedAddressDetails?.Street2
            : '',
            onChange: (e:any) =>
              updateAddressDetailsInQnTab(
                { Street2: e.target.value },
                qnTempId,
              ),
          })}
          {renderTextField({
            id: 'street3',
            label: 'Street 3',
            value: formattedAddressDetails?.Street3
            ? formattedAddressDetails?.Street3
            : '',
            onChange: (e:any) =>
              updateAddressDetailsInQnTab(
                { Street3: e.target.value },
                qnTempId,
              ),
          })}
          {renderTextField({
            id: 'city',
            label: 'City 3',
            value: formattedAddressDetails?.City
            ? formattedAddressDetails?.City
            : '',
            onChange: (e:any) =>
              updateAddressDetailsInQnTab(
                { City: e.target.value },
                qnTempId,
              ),
          })}
          <Select
            id="state/Province"
            options={stateProvinceDropDownList}
            label="State/Province"
            // key="StateProvince"
            value={
              formattedAddressDetails?.StateProvince?.Id
                ? formattedAddressDetails?.StateProvince?.Id
                : ''
            }
            onChange={e => {
              if (Array.isArray(stateprovince)) {
                updateAddressDetailsInQnTab(
                  {
                    StateProvinceId: Number(e.target.value),
                    StateProvince: stateprovince.find(
                      item => item.Id === Number(e.target.value),
                    ),
                  },
                  qnTempId,
                );
              }
            }}
          />
           {renderTextField({
            id: 'Zip/Postal Code',
            label: 'Zip/Postal code 3',
            value: formattedAddressDetails?.ZipPostalCode
            ? formattedAddressDetails?.ZipPostalCode
            : '',
            onChange: (e:any) =>
              updateAddressDetailsInQnTab(
                { ZipPostalCode: e.target.value },
                qnTempId,
              ),
          })}
          <Select
            id="country"
            options={countryDropDownList}
            //  key="Country"
            value={formattedAddressDetails?.CountryId}
            label="Country"
            onChange={e => {
              if (Array.isArray(country)) {
                updateAddressDetailsInQnTab(
                  {
                    CountryId: Number(e.target.value),
                    Country: country.find(
                      item => item.Id === Number(e.target.value),
                    ),
                  },
                  qnTempId,
                );
              }
            }}
          />
          {renderTextField({
            id: 'county',
            label: 'County',
            value: formattedAddressDetails?.County
            ? formattedAddressDetails?.County
            : '',
            onChange: (e:any) =>
              updateAddressDetailsInQnTab(
                { County: e.target.value },
                qnTempId,
              ),
          })}
        </div>
      </p>
    </dd>
  );
}
